import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
//import { FeeStage, FeeStageComment, OutstandingRows, ProjectForecast } from '../../interfaces/forecast.interface';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ForecastDisplayType } from '../../constants/constants';
import dayjs, { Dayjs } from 'dayjs';
import { Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography } from '@mui/material';
import { AddCircleIcon, AddCommentIcon, CommentIcon, formatCurrency, getStickyStyle, KeyboardArrowDown, KeyboardArrowUp, parseMonthYearToUTCDate, Projects, RemoveCircleIcon, Toaster, useApi, useLocation, useNavigate } from '../../utils/globalImports';
import React from 'react';
import { ApiResponse } from '../../services/apiService';
import ResizeHandle from '../Common/ResizeHandle';
import { IFSCategory, IFSFeeStage, IFSFeeStageComment, IFSForecast, IFSOutstandingRows, IFSProject, IFSProposal, IFSSubcategory } from '../../interfaces/IFSForecast.interfaces';
import ForecastCommentModal from './ForecastCommentModal';
import { Category, Subcategory } from '../../interfaces/baselineInterface';
import { sumAllProjectsMonthlyTotal, sumCategoryMonthlyTotal, sumProjectMonthlyTotal, sumProposalMonthlyTotal, sumSubcategoryMonthlyTotal } from './IFSCalculationHelper';
interface ActiveMonthDetails{
  year:number,
  month:number,
  currentLockDate:Date,
  minAllowedDate:Date,
  maxAllowedDate:Date
}
type ColWidths = ReturnType<typeof useColWidths>[0];

// const useColWidths = () => {
//     return useState({
//         expand: 80,
//         second: 50,
//         code: 100,
//         item: 200,
//         cost: 120,
//         quantity: 100,
//         totalCost: 100,
//         outstanding: 150,
//         thirdPartyCost: 350,
//     });
// };
const useColWidths = () => {
    return useState({
        code: 120,
        item: 580,   // slightly wider since it holds icon + text
        cost: 120,
        quantity: 100,
        totalCost: 100,
        outstanding: 150,
        thirdPartyCost: 350,
    });
};



const commonHeaderStyle = {
    '& th': {
        fontSize: '12px',
        fontWeight: 'bold',
        border: '1px solid rgba(0, 0, 0, 0.04)',
        backgroundColor: '#92d050',
        whiteSpace: 'nowrap',
    },
};

const commonRowStyle = {
    '& td': {
        fontSize: '14px',
        border: '1px solid rgba(0, 0, 0, 0.04)',
        color: '#1e66ab',
        //fontWeight: 'bold',
    },
};
const childStyle = {
    fontSize: '16px',
    fontWeight: 'bold',
}
const parentStyle = {
    fontSize: '17px',
    fontWeight: 'bold',
}

interface ModalProps {
    forecastData: IFSProject[];
    startDate: string;
    activeMonthDetails:ActiveMonthDetails |null,
    endDate: string;
    forecastType: number;
    readOnly: boolean;
    isCompareEligible: boolean;
}

interface CustomIFSTrackerRef {
    triggerSave: () => void;
}

interface CollapsibleRowProps {
    row: IFSProject;
    monthColumns: GridColDef[];
    rowStyle: any;
    monthHeaders: any[];
    onForecastChange: (feeStageID: number, month: string, value: number) => void;
    flattenedForecastMap: Record<number, Record<string, number> & IFSFeeStage>;
    setToast: React.Dispatch<React.SetStateAction<{ open: boolean; message: string; type: string }>>;
    handleCommentClick: (feestage: IFSFeeStageComment) => void;
    forecastType?: number;
    readOnly: boolean;
    colWidths: ColWidths;
    getLeftOffset: (key: keyof ColWidths, forecastType?: ForecastDisplayType) => number; // <-- also here
}

const generateMonthHeaders = (startDate: Dayjs, endDate: Dayjs): string[] => {
    const monthHeaders: string[] = [];
    let current = startDate.startOf('month');

    while (current.isBefore(endDate) || current.isSame(endDate, 'month')) {
        monthHeaders.push(current.format('MMM-YYYY')); // e.g., "Aug-2024"
        current = current.add(1, 'month');
    }

    return monthHeaders;
};
const getFeeStageRemaining = (fee: IFSFeeStage) => {
    const forecastSum =
        fee.forecasts?.reduce((s, f) => s + (f.forecastValue ?? 0), 0) ?? 0;

    return (fee.totalCost ?? 0) - forecastSum;
};

const sumRemainingFromFeeStages = (feeStages?: IFSFeeStage[]) =>
    feeStages?.reduce((sum, f) => sum + getFeeStageRemaining(f), 0) ?? 0;






const fillForecastRow = (
    feeStage: IFSFeeStage,
    projectStart: string,
    projectEnd: string,
    monthHeaders: string[]
): Record<string, any> => {
    const row: Record<string, any> = {
        feeStageID: feeStage.feeStageID,
        feeStageReferenceNumber: feeStage.feeStageReferenceNumber,
        description: feeStage.feeStageName,
        forecasts: feeStage.forecasts,
    };

    const start = dayjs(projectStart).startOf("month");
    const end = dayjs(projectEnd).endOf("month");

    for (const header of monthHeaders) {
        const month = dayjs(header, "MMM-YYYY");

        if (month.isBefore(start) || month.isAfter(end)) {
            row[header] = "N/A"; // 🚫 outside project range
        } else {
            row[header] =
                feeStage.forecasts?.find(
                    (f) => dayjs(f.monthDate).format("MMM-YYYY") === header
                )?.forecastValue ?? null; // ✅ inside range
        }
    }

    return row;
};



// 2. Flatten forecasts into top-level fields
const flattenForecasts = (stage: IFSFeeStage) => {
    const forecastMap: { [key: string]: number } = {};

    stage.forecasts.forEach(f => {
        const monthKey = dayjs(f.monthDate).format('MMM-YYYY');
        forecastMap[monthKey] = f.forecastValue;
    });

    return {
        ...stage,
        ...forecastMap,
    };
};

// 3. Generate columns for each forecast month
export const getMonthColumns = (
    monthHeaders: string[],
    onCommentClick: (feestage: IFSFeeStageComment) => void,
    readOnly?: boolean,
    forecastType?: number,
    isCompareEligible?: boolean
): GridColDef[] =>
    monthHeaders.map((month) => ({
        field: month,
        headerName: month,
        minWidth: 150,
        editable: true,
        type: 'number',


        renderCell: (params: GridRenderCellParams) => {
            const value = params.row[month];
            const forecasts = params.row.forecasts ?? [];

            // Find the forecast that matches the current column month
            const forecast = forecasts.find((f: any) => {
                const formattedMonth = dayjs(f.monthDate).format('MMM-YYYY');
                return formattedMonth === month;
            });
            const difference = forecast?.differenceValue;
            const percentage = forecast?.changePercentage;


            const isEnabled = forecast?.isEnabledViewComment;
            const isEditable = forecast ? !forecast.isInvoiced && !forecast.isAutoLock : true;
            // 👉 Hide comment icon if value is "N/A"
            const showComment = value !== "N/A";
            return (
                <Box sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 50, // ensures enough space for the icon
                    width: '100%',
                    ...(isEditable
                        ? {}
                        : {
                            backgroundColor: '#fddede',
                        }),
                }}>
                    <Typography
                        variant="body2"
                        sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                    >
                        {value === "N/A" ? (
                            "N/A"
                        ) : isCompareEligible ? (
                            <>
                                {/* Difference */}
                                <span>
                                    <span>{formatCurrency(difference ?? 0)}</span>
                                </span>

                                {/* Separator */}
                                <span style={{ fontSize: '25px', color: '#666' }}>
                                    |
                                </span>

                                {/* Percentage */}
                                <span style={{ fontSize: '14px', color: '#1e66ab', paddingRight: '30px' }}>
                                    {percentage ?? 0}%
                                </span>
                            </>
                        ) : (
                            /* Default forecast value */
                            <span>{formatCurrency(value ?? 0)}</span>
                        )}
                    </Typography>
                    {showComment && (
                        <IconButton
                            color="primary"
                            size="small"
                            disabled={readOnly}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                            }}
                            onClick={() => {
                                const comment: IFSFeeStageComment = {
                                    id: params.row.feestageID,
                                    feeStageReferenceNumber: params.row.feeStageReferenceNumber,
                                    description: params.row.description,
                                    month,
                                    hasComments: isEnabled,
                                    feeStageID: params.row.feeStageID,  // extra
                                    displayType: forecastType           // extra
                                }
                                onCommentClick(comment)
                            }}
                        >
                            {isEnabled ? (
                                <CommentIcon sx={{ color: '#1e66ab' }} />
                            ) : (
                                <AddCommentIcon sx={{ color: '#a3c63d' }} />
                            )}
                        </IconButton>
                    )}
                </Box >
            );
        }
    }));

// }

const getFeeStageDifference = (feeStage: IFSFeeStage) => {
    const forecastSum =
        feeStage.forecasts?.reduce(
            (sum, f) => sum + (f.forecastValue ?? 0),
            0
        ) ?? 0;

    return (feeStage.totalCost ?? 0) - forecastSum;
};


// calculate total when page load 
const calculateTotals = (forecastData: IFSProject[]) => {
    let totalCost = 0;
    let totalQuantity = 0;
    let totalTotalCost = 0;
    let totalOutstanding = 0;
    let totalThirdPartyCost = 0;
    let totalDifference = 0;

    forecastData.forEach((project) => {
        project.proposals?.forEach((proposal) => {
            proposal.categories?.forEach((category) => {
                category.subcategories?.forEach((subcat) => {
                    subcat.feeStages?.forEach((fee) => {
                        const diff = getFeeStageDifference(fee);
                        totalTotalCost += fee.totalCost ?? 0;
                        totalDifference += getFeeStageDifference(fee);

                    });
                });
            });
        });
    });
    return {
        totalCost,
        totalQuantity,
        totalTotalCost,
        totalOutstanding,
        totalThirdPartyCost,
        totalDifference,
    };
};

const CollapsibleRow = ({
    row,
    monthColumns,
    rowStyle,
    monthHeaders,
    onForecastChange,
    flattenedForecastMap,
    setToast,
    handleCommentClick,
    forecastType,
    readOnly,
    colWidths,
    getLeftOffset
}: CollapsibleRowProps) => {

    const [openProjects, setOpenProjects] = useState<Record<number, boolean>>({});
    const [openProposals, setOpenProposals] = useState<Record<number, boolean>>({});
    const [openCategories, setOpenCategories] = useState<Record<number, boolean>>({});
    const [openSubcategories, setOpenSubcategories] = useState<Record<number, boolean>>({});
    const navigate = useNavigate();
    const location = useLocation();

    return (

        <React.Fragment>
            {/* Project ROW */}
            <TableRow sx={rowStyle}>

                <TableCell
                    sx={{
                        ...getStickyStyle(getLeftOffset("code"), colWidths.code),
                        minWidth: colWidths.code,
                        width: colWidths.code,
                        gap: 1,
                        pl: 2,
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={() =>
                            setOpenProjects(prev => ({
                                ...prev,
                                [row.projectID]: !prev[row.projectID],
                            }))
                        }
                    >
                        {openProjects[row.projectID] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell
                    sx={{
                        ...getStickyStyle(getLeftOffset("item"), colWidths.item),
                        minWidth: colWidths.item,
                        width: colWidths.item,
                    }}
                >
                    <Typography style={parentStyle}
                        sx={{ cursor: "pointer", color: "#1e66ab" }}
                        onClick={() =>
                            navigate(`/project/edit-projects/${row.projectID}`, {
                                state: {
                                    from: location.pathname,
                                    fromTab: forecastType,   // 👈 saves /ifs-tracker
                                    expandedProjectId: row.projectID,
                                }
                            })
                        }
                    >
                        {row.projectNumber}-{row.clientName}{" "}{row.projectName}
                    </Typography>
                </TableCell>
                <>
                    <TableCell
                        sx={{
                            ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                            minWidth: colWidths.cost,
                            width: colWidths.cost,
                            fontWeight: "bold",

                        }}
                    >
                        <Typography style={parentStyle} >
                            {formatCurrency(
                                row.proposals?.reduce(
                                    (pSum, p) =>
                                        pSum +
                                        p.categories?.reduce(
                                            (cSum, c) =>
                                                cSum +
                                                c.subcategories?.reduce(
                                                    (sSum, s) => sSum + sumRemainingFromFeeStages(s.feeStages),
                                                    0
                                                ),
                                            0
                                        ),
                                    0
                                )
                            )}
                        </Typography>

                    </TableCell>
                    {monthHeaders.map((month) => (
                        <TableCell
                            key={month}
                            sx={{ minWidth: 150, width: 150, textAlign: "center", verticalAlign: "middle", fontWeight: "bold", fontSize: 22 }}
                        >
                            <Typography style={parentStyle}>
                                {formatCurrency(sumProjectMonthlyTotal(row, month))}
                            </Typography>
                        </TableCell>
                    ))}


                </>
            </TableRow>
            {openProjects[row.projectID] &&
                row.proposals?.map((proposal) => (
                    <React.Fragment key={proposal.proposalID}>
                        {/* PROPOSAL ROW */}
                        <TableRow sx={rowStyle}>
                            <TableCell
                                sx={{
                                    ...getStickyStyle(getLeftOffset("code"), colWidths.code),
                                    minWidth: colWidths.code,
                                    width: colWidths.code,
                                    gap: 1,
                                    pl: 4,
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        setOpenProposals(prev => ({
                                            ...prev,
                                            [proposal.proposalID]: !prev[proposal.proposalID],
                                        }))
                                    }
                                >
                                    {openProposals[proposal.proposalID] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...getStickyStyle(getLeftOffset("item"), colWidths.item),
                                    minWidth: colWidths.item,
                                    width: colWidths.item,
                                }}
                            >
                                <Typography style={childStyle}
                                    sx={{ cursor: "pointer", color: "#1e66ab" }}
                                    onClick={() =>
                                        navigate(
                                            `/proposal/add-proposal/${row.projectID}/${proposal.proposalID}/${row.projectName}`,
                                            {
                                                state: {
                                                    from: location.pathname,
                                                    fromTab: forecastType,
                                                },
                                            }
                                        )
                                    }>
                                    {proposal.proposalNumber}
                                </Typography>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                                    minWidth: colWidths.cost,
                                    width: colWidths.cost,
                                    fontWeight: "bold"
                                }}
                            >
                                <Typography style={childStyle}>
                                    {formatCurrency(
                                        proposal.categories?.reduce(
                                            (cSum, c) =>
                                                cSum +
                                                c.subcategories?.reduce(
                                                    (sSum, s) => sSum + sumRemainingFromFeeStages(s.feeStages),
                                                    0
                                                ),
                                            0
                                        )
                                    )}
                                </Typography>
                            </TableCell>
                            {monthHeaders.map(month => (
                                <TableCell key={month}
                                    sx={{

                                        textAlign: "center",        // horizontal center
                                        verticalAlign: "middle",    // vertical center
                                        fontWeight: "bold"
                                    }}>
                                    <Typography style={childStyle}>
                                        {formatCurrency(sumProposalMonthlyTotal(proposal, month))}
                                    </Typography>
                                </TableCell>
                            ))}

                        </TableRow>

                        {/* CATEGORY LEVEL */}
                        {openProposals[proposal.proposalID] &&
                            proposal.categories?.map((category) => (
                                <React.Fragment key={category.categoryID}>
                                    <TableRow sx={rowStyle}>
                                        <TableCell
                                            sx={{
                                                ...getStickyStyle(getLeftOffset("code"), colWidths.code),
                                                minWidth: colWidths.code,
                                                width: colWidths.code,
                                                gap: 1,
                                                pl: 6,
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setOpenCategories((prev) => ({
                                                        ...prev,
                                                        [category.categoryID]: !prev[category.categoryID],
                                                    }))
                                                }
                                            >
                                                {openCategories[category.categoryID] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...getStickyStyle(getLeftOffset("item"), colWidths.item),
                                                minWidth: colWidths.item,
                                                width: colWidths.item,
                                            }}
                                        >

                                            <Typography style={childStyle}>
                                                {category.categoryName}
                                            </Typography>
                                        </TableCell>
                                        <>
                                            <TableCell
                                                sx={{
                                                    ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                                                    minWidth: colWidths.cost,
                                                    width: colWidths.cost,
                                                    fontWeight: "bold"
                                                }}
                                            >
                                                <Typography style={childStyle}>
                                                    {
                                                        formatCurrency(
                                                            category.subcategories?.reduce(
                                                                (sSum, s) => sSum + sumRemainingFromFeeStages(s.feeStages),
                                                                0
                                                            )
                                                        )}
                                                </Typography>

                                            </TableCell>
                                            {monthHeaders.map(month => (
                                                <TableCell key={month} sx={{

                                                    textAlign: "center",        // horizontal center
                                                    verticalAlign: "middle",    // vertical center
                                                    fontWeight: "bold"
                                                }}>
                                                    <Typography style={childStyle}>
                                                        {formatCurrency(sumCategoryMonthlyTotal(category, month))}
                                                    </Typography>
                                                </TableCell>
                                            ))}


                                        </>
                                    </TableRow>

                                    {/* SUBCATEGORY LEVEL */}
                                    {openCategories[category.categoryID] &&
                                        category.subcategories?.map((subcategory) => (
                                            <React.Fragment key={subcategory.subcategoryID}>
                                                <TableRow sx={rowStyle}>
                                                    <TableCell
                                                        sx={{
                                                            ...getStickyStyle(getLeftOffset("code"), colWidths.code),
                                                            minWidth: colWidths.code,
                                                            width: colWidths.code,
                                                            gap: 1,
                                                            pl: 8,
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                setOpenSubcategories((prev) => ({
                                                                    ...prev,
                                                                    [subcategory.subcategoryID]: !prev[subcategory.subcategoryID],
                                                                }))
                                                            }
                                                        >
                                                            {openSubcategories[subcategory.subcategoryID] ? (
                                                                <KeyboardArrowUp />
                                                            ) : (
                                                                <KeyboardArrowDown />
                                                            )}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...getStickyStyle(getLeftOffset("item"), colWidths.item),
                                                            minWidth: colWidths.item,
                                                            width: colWidths.item,

                                                        }}
                                                    >

                                                        <Typography variant="h6" style={childStyle}>
                                                            {subcategory.subcategoryName}
                                                        </Typography>
                                                    </TableCell>
                                                    <>
                                                        <TableCell
                                                            sx={{
                                                                ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                                                                minWidth: colWidths.cost,
                                                                width: colWidths.cost,
                                                                fontWeight: "bold"
                                                            }}>
                                                            <Typography style={childStyle}>
                                                                {formatCurrency(sumRemainingFromFeeStages(subcategory.feeStages))}
                                                            </Typography>
                                                        </TableCell>
                                                        {monthHeaders.map(month => (
                                                            <TableCell key={month} sx={{

                                                                textAlign: "center",        // horizontal center
                                                                verticalAlign: "middle",    // vertical center
                                                                fontWeight: "bold"
                                                            }}>
                                                                <Typography style={childStyle}>
                                                                    {formatCurrency(sumSubcategoryMonthlyTotal(subcategory, month))}
                                                                </Typography>
                                                            </TableCell>
                                                        ))}

                                                    </>
                                                </TableRow>

                                                {/* FEESTAGE LEVEL */}
                                                {openSubcategories[subcategory.subcategoryID] &&
                                                    subcategory.feeStages?.map((feeStage) => (
                                                        <TableRow key={feeStage.feeStageID} sx={{
                                                            ...rowStyle,
                                                            ...(readOnly && {
                                                                backgroundColor: "#f0f0f0",   // grey background
                                                                color: "#9e9e9e",             // grey text
                                                                pointerEvents: "none",        // disable all interactions
                                                                opacity: 0.7,                 // make it look disabled
                                                            }),
                                                        }}>

                                                            <TableCell
                                                                sx={{
                                                                    ...getStickyStyle(getLeftOffset("code"), colWidths.code),
                                                                    minWidth: colWidths.code,
                                                                    width: colWidths.code,
                                                                    fontSize: "18px",

                                                                }}
                                                            > <Typography fontSize={12}>
                                                                    {feeStage.feeStageReferenceNumber}
                                                                </Typography></TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    ...getStickyStyle(getLeftOffset("item"), colWidths.item),
                                                                    minWidth: colWidths.item,
                                                                    width: colWidths.item,
                                                                    pl: 10,
                                                                    cursor: "pointer", color: "#1e66ab", fontSize: "18px",
                                                                }}
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/proposal/feestage/edit-feestage/${feeStage.feeStageID}/false`,
                                                                        {
                                                                            state: {
                                                                                from: location.pathname,
                                                                                fromTab: forecastType,
                                                                            },
                                                                        }
                                                                    )
                                                                }
                                                            ><Typography fontSize={12}>
                                                                    {feeStage.feeStageName}
                                                                </Typography></TableCell>
                                                            <>
                                                                <TableCell
                                                                    sx={{
                                                                        ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                                                                        minWidth: colWidths.cost,
                                                                        width: colWidths.cost,
                                                                        fontWeight: "bold"
                                                                    }}
                                                                >{formatCurrency(getFeeStageDifference(feeStage))}</TableCell>
                                                            </>


                                                            {/* Monthly Forecast */}
                                                            <TableCell colSpan={monthHeaders.length} sx={{ padding: 0 }}>

                                                                <Box sx={{ overflowX: 'auto' }}>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'inline-block',
                                                                            minWidth: monthHeaders.length * 150,
                                                                            verticalAlign: 'top',
                                                                            '& .MuiDataGrid-root': { border: 'none' },
                                                                            '& .MuiDataGrid-columnHeaders': {
                                                                                display: 'none',
                                                                                height: 0,
                                                                            },
                                                                            '& .MuiDataGrid-row': {
                                                                                maxHeight: 36,
                                                                                minHeight: 36,
                                                                            },
                                                                            '& .MuiDataGrid-cell': {
                                                                                fontSize: '14px',
                                                                                fontWeight: "bold",
                                                                                padding: '4px 8px',
                                                                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                                                                color: '#1e66ab',
                                                                            },
                                                                            '& .MuiDataGrid-virtualScroller': {
                                                                                overflow: 'visible !important',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <DataGrid
                                                                            autoHeight
                                                                            hideFooter
                                                                            //rows={[flattenedForecastMap[feeStage.feeStageID] ?? flattenForecasts(feeStage)]}
                                                                            rows={[
                                                                                fillForecastRow(
                                                                                    feeStage,
                                                                                    row.startDate,       // pass project’s start date
                                                                                    row.endDate,         // pass project’s end date
                                                                                    monthHeaders
                                                                                )
                                                                            ]}
                                                                            columns={monthColumns}
                                                                            getRowId={(row) => row.feeStageID}
                                                                            isCellEditable={() => false}
                                                                            processRowUpdate={(newRow, oldRow) => {
                                                                                if (readOnly) return oldRow;
                                                                                const changedMonth = Object.keys(newRow).find(
                                                                                    (key) => newRow[key] !== oldRow[key] && monthHeaders.includes(key)
                                                                                );
                                                                                return newRow;
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                {/* SUBCATEGORY TOTAL */}
                                                {openSubcategories[subcategory.subcategoryID] &&
                                                    (subcategory.feeStages?.length ?? 0) > 1 && (
                                                        <TableRow sx={{ backgroundColor: "#f5f5f5" }} >

                                                            <TableCell
                                                                sx={{
                                                                    ...getStickyStyle(getLeftOffset("code"), colWidths.code),
                                                                    minWidth: colWidths.code,
                                                                    width: colWidths.code,
                                                                }}
                                                            />
                                                            <TableCell
                                                                sx={{
                                                                    ...getStickyStyle(getLeftOffset("item"), colWidths.item),
                                                                    minWidth: colWidths.item,
                                                                    width: colWidths.item,
                                                                    fontWeight: "bold",
                                                                    fontSize: "14px",
                                                                }}
                                                            >
                                                                Subcategory Total:
                                                            </TableCell>
                                                            <>
                                                                <TableCell
                                                                    sx={{
                                                                        ...getStickyStyle(getLeftOffset("cost"), colWidths.cost),
                                                                        minWidth: colWidths.cost,
                                                                        width: colWidths.cost,
                                                                        fontWeight: "bold",
                                                                    }}
                                                                >
                                                                    {formatCurrency(
                                                                        subcategory.feeStages?.reduce(
                                                                            (sum, f) => sum + (f.totalCost || 0),
                                                                            0
                                                                        )
                                                                    )}
                                                                </TableCell>
                                                                {/* 🔥 Month Totals */}
                                                                {monthHeaders.map(month => (
                                                                    <TableCell
                                                                        key={month}
                                                                        sx={{
                                                                            fontWeight: "bold",
                                                                            textAlign: "center",        // horizontal center
                                                                            verticalAlign: "middle",    // vertical center
                                                                        }}
                                                                    >
                                                                        {formatCurrency(
                                                                            sumSubcategoryMonthlyTotal(subcategory, month)
                                                                        )}
                                                                    </TableCell>
                                                                ))}
                                                            </>
                                                        </TableRow>
                                                    )}
                                            </React.Fragment>
                                        ))}
                                </React.Fragment>
                            ))}
                    </React.Fragment>
                ))}
        </React.Fragment>
    );
};
const CustomIFSTracker = forwardRef(({
    forecastData,
    startDate,
    endDate,
    activeMonthDetails,
    forecastType,
    readOnly,
    isCompareEligible,
}: ModalProps, ref) => {

    const { execute } = useApi();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [updatedRows, setUpdatedRows] = useState<any[]>([]);
    const [flattenedForecastMap, setFlattenedForecastMap] = useState<Record<number, any>>({});
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [showHiddenMonths, setShowHiddenMonths] = useState(false);
    const [openCommentModal, setOpenCommentModal] = useState(false);
    const [selectedComment, setSelectedComment] = useState<{ feestage: IFSFeeStageComment } | null>(null);
    const [onModalComplete, setOnModalComplete] = useState<((commentAdded: boolean) => void) | null>(null);

    // const effectiveStartDate = useMemo(() => {
    //     const projectStart = dayjs(startDate).startOf("month");
    //    let currentMonth = activeMonthDetails ? dayjs(`${activeMonthDetails.year}-${activeMonthDetails.month}-01`).startOf("month"): dayjs().startOf("month");
        
       
        

    //     const ifsDay = forecastData?.[0]?.ifsStartDay;
    //     const today = dayjs();


    //     if (ifsDay && today.date() < dayjs(ifsDay).date()) {
    //         currentMonth = currentMonth.subtract(1, "month");
    //     }

    //     if (!showHiddenMonths) {
    //         return projectStart.isAfter(currentMonth)
    //             ? projectStart
    //             : currentMonth;
    //     }

    //     return projectStart;

    // }, [startDate, showHiddenMonths, forecastData]);
    const effectiveStartDate = useMemo(() => {
    const projectStart = dayjs(startDate).startOf("month");

    let currentMonth = activeMonthDetails
        ? dayjs(
            `${activeMonthDetails.year}-${activeMonthDetails.month}-01`
          ).startOf("month")
        : dayjs().startOf("month");

    const ifsDay = forecastData?.[0]?.ifsStartDay;
    const today = dayjs();

    // Apply IFS logic only when activeMonthDetails is not present
    if (!activeMonthDetails && ifsDay && today.date() < dayjs(ifsDay).date()) {
        currentMonth = currentMonth.subtract(1, "month");
    }

    if (!showHiddenMonths) {
        return projectStart.isAfter(currentMonth)
            ? projectStart
            : currentMonth;
    }

    return projectStart;
}, [
    startDate,
    showHiddenMonths,
    forecastData,
    activeMonthDetails
]);
    //const monthHeaders = generateMonthHeaders(dayjs(startDate), dayjs(endDate));
    const monthHeaders = useMemo(() => {
        return generateMonthHeaders(
            effectiveStartDate,
            dayjs(endDate)
        );
    }, [effectiveStartDate, endDate]);

    const [colWidths, setColWidths] = useColWidths();

    const handleCommentClick = (feestage: IFSFeeStageComment) => {
        setSelectedComment({ feestage });
        setOpenCommentModal(true);
    };

    // const monthColumns = useMemo(() => getMonthColumns(monthHeaders, handleCommentClick),
    //   [monthHeaders, flattenedForecastMap]);

    // const visibleMonths = useMemo(() => {
    //     const columns = getMonthColumns(monthHeaders, handleCommentClick, readOnly, forecastType, isCompareEligible);

    //     if (!showHiddenMonths) {
    //         // Return all columns without filtering
    //         return columns;
    //     }

    //     // Apply filter only when showHiddenMonths is false
    //     return columns.filter((col) =>
    //         Object.values(flattenedForecastMap).some((row) =>
    //             (col as any).isLocked?.(row)
    //         )
    //     );
    // }, [monthHeaders, flattenedForecastMap, showHiddenMonths, forecastType]);
    const monthColumns = useMemo(() => {
        return getMonthColumns(
            monthHeaders,
            handleCommentClick,
            readOnly,
            forecastType,
            isCompareEligible
        );
    }, [monthHeaders, readOnly, forecastType, isCompareEligible]);

    const visibleMonthsHeader = monthHeaders;


    // const visibleMonthsHeader = visibleMonths
    //     .map((col) => col.headerName);

    const userId = Number(localStorage.getItem("userId"));

    useEffect(() => {
        const map: Record<number, any> = {};
        forecastData.forEach((project) =>
            project.proposals.forEach((proposal) =>
                proposal.categories.forEach((cat) =>
                    cat.subcategories.forEach((sub) =>
                        sub.feeStages.forEach((stage) => {
                            map[stage.feeStageID] = flattenForecasts(stage);
                        })
                    )
                )
            )
        );
        setFlattenedForecastMap(map);
    }, [forecastData]);
    const handleForecastChange = (feeStageID: number, month: string, value: number) => {
    };

    const paginatedRows = forecastData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const { totalCost, totalQuantity, totalTotalCost, totalThirdPartyCost, totalDifference } = useMemo(
        () => calculateTotals(forecastData),
        [forecastData]
    );

    const getLeftOffset = (key: keyof typeof colWidths, forecastType?: ForecastDisplayType) => {
        let keys: (keyof typeof colWidths)[];

        if (forecastType === ForecastDisplayType.ThirdPartyCost) {
            // Include the two fixed expand columns in the offset calculation
            keys = ["code", "item", "thirdPartyCost", "outstanding"];
        } else {
            keys = ["code", "item", "cost", "quantity", "totalCost", "outstanding"];
        }

        let offset = 0;
        for (const k of keys) {
            if (k === key) break;
            if (k === "thirdPartyCost") {
                offset += colWidths.cost + colWidths.quantity + colWidths.totalCost;
            } else {
                offset += colWidths[k] ?? 50;
            }
        }
        return offset;
    };

    return (
        <>
            <Paper sx={{
                width: '100%',
                height: '100%', // Fill the parent Box
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Box display="flex" justifyContent="left">
                        <IconButton
                            color="primary"
                            aria-label="toggle months"
                            onClick={() => setShowHiddenMonths(prev => !prev)}
                        >
                            {showHiddenMonths ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <RemoveCircleIcon sx={{ color: '#a3c63d' }} />
                                    <Typography variant="body2">Hide Previous Months</Typography>
                                </Box>
                            ) : (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AddCircleIcon sx={{ color: '#a3c63d' }} />
                                    <Typography variant="body2">Show Previous Months</Typography>
                                </Box>
                            )}
                        </IconButton>
                    </Box>
                </Box>

                <TableContainer
                    sx={{
                        // minHeight: 0,
                        // maxHeight: '70vh',        // important
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'auto',
                    }}
                >
                    <Table stickyHeader>
                        <TableHead
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 6,
                            }}
                        >
                            <TableRow sx={commonHeaderStyle}>
                                {/* <TableCell
                                    sx={{ ...getStickyStyle(getLeftOffset("expand", forecastType), 50, true), minWidth: 80, width: 50, position: "sticky", top: 0, zIndex: 5, }}
                                />
                                <TableCell
                                    sx={{ ...getStickyStyle(getLeftOffset("second", forecastType), 50, true), minWidth: 50, width: 50, position: "sticky", top: 0, zIndex: 5, }}
                                /> */}
                                {/* Code */}
                                <TableCell
                                    sx={{ ...getStickyStyle(getLeftOffset("code"), colWidths.code, true), minWidth: colWidths.code, width: colWidths.code, position: "sticky", top: 0, zIndex: 5, }}
                                >
                                    Code
                                    <ResizeHandle
                                        onResize={(delta) =>
                                            setColWidths((prev) => ({
                                                ...prev,
                                                code: Math.max(prev.code + delta, 50),
                                            }))
                                        }
                                    />
                                </TableCell>

                                {/* Item */}
                                <TableCell
                                    sx={{ ...getStickyStyle(getLeftOffset("item"), colWidths.item, true), minWidth: colWidths.item, width: colWidths.item, position: "sticky", top: 0, zIndex: 5, }}
                                >
                                    Item
                                    <ResizeHandle
                                        onResize={(delta) =>
                                            setColWidths((prev) => ({
                                                ...prev,
                                                item: Math.max(prev.item + delta, 50),
                                            }))
                                        }
                                    />
                                </TableCell>
                                <>
                                    {/* Cost */}
                                    <TableCell
                                        sx={{
                                            ...getStickyStyle(getLeftOffset("cost"), colWidths.cost, true), minWidth: colWidths.cost, width: colWidths.cost, position: "sticky", top: 0, zIndex: 5,
                                        }}
                                    >
                                        Remaining fee
                                        <ResizeHandle
                                            onResize={(delta) =>
                                                setColWidths((prev) => ({
                                                    ...prev,
                                                    cost: Math.max(prev.cost + delta, 50),
                                                }))
                                            }
                                        />
                                    </TableCell>
                                </>
                                {/* Months scrollable */}
                                {visibleMonthsHeader.map((month) => (
                                    <TableCell key={month} sx={{ minWidth: 150, width: 150 }}>
                                        {month}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRows?.length ? (
                                paginatedRows.map((row) => (
                                    <CollapsibleRow
                                        key={row.projectNumber}
                                        row={row}
                                        monthColumns={monthColumns}
                                        rowStyle={commonRowStyle}
                                        monthHeaders={visibleMonthsHeader}
                                        onForecastChange={handleForecastChange}
                                        flattenedForecastMap={flattenedForecastMap}
                                        setToast={setToast}
                                        handleCommentClick={handleCommentClick}
                                        forecastType={forecastType}
                                        readOnly={readOnly}
                                        colWidths={colWidths}
                                        getLeftOffset={getLeftOffset}
                                    />
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell align="center">
                                        No data available
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                        {/* Footer inside Table */}
                        <TableFooter
                            sx={{ position: 'sticky', bottom: 0, zIndex: 3, }}>
                            <TableRow sx={{
                                '& td': { ontSize: '18px', color: 'black', fontWeight: 'bold', border: '1px solid rgba(0, 0, 0, 0.04)', },
                            }} >
                                {/* <TableCell
                                    sx={{ ...getStickyStyle(getLeftOffset("expand", forecastType), 50), minWidth: 50, width: 50, position: "sticky", }}
                                />
                                <TableCell
                                    sx={{ ...getStickyStyle(getLeftOffset("second", forecastType), 50), minWidth: 50, width: 50, position: "sticky", }}
                                /> */}
                                <TableCell
                                    sx={{ ...getStickyStyle(getLeftOffset("code"), colWidths.code), minWidth: colWidths.code, width: colWidths.code, }}
                                ></TableCell>
                                <TableCell
                                    sx={{ ...getStickyStyle(getLeftOffset("item"), colWidths.item), minWidth: colWidths.item, width: colWidths.item, }}
                                >Total
                                </TableCell>

                                <>
                                    <TableCell
                                        sx={{ ...getStickyStyle(getLeftOffset("cost"), colWidths.cost), minWidth: colWidths.cost, width: colWidths.cost, }}
                                    >{totalDifference.toLocaleString()}</TableCell>
                                </>
                                {monthHeaders.map(month => (
                                    <TableCell
                                        key={month}
                                        sx={{
                                            textAlign: "center",
                                            fontWeight: "bold",
                                            backgroundColor: 'white'
                                        }}
                                    >
                                        {formatCurrency(
                                            sumAllProjectsMonthlyTotal(forecastData, month)
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </Paper>

            {openCommentModal && selectedComment && (
                <ForecastCommentModal
                    open={openCommentModal}
                    feestage={selectedComment?.feestage ?? null}
                    onClose={(commentAdded, commentText) => {
                        if (commentAdded && selectedComment) {
                            console.log("Comment saved:", commentText);
                        }
                        setOpenCommentModal(false);
                        setSelectedComment(null);
                    }}
                />
            )}
            <Toaster
                open={toast.open}
                message={toast.message}
                type={toast.type as "success" | "error"}
                onClose={() =>
                    setToast({ ...toast, open: false })
                }
            />
        </>
    );
});

export default CustomIFSTracker;
